// The offline core — one entry point (startOffline) and the public barrel.
//
// startOffline() is called ONCE from main.tsx, after the session boot is kicked
// off. It wires the outbox counter into the logout guard and starts
// connectivity tracking and the sync engine.
//
// WHEN THE READ-SIDE DATA IS PULLED — the part that is easy to make expensive
// by accident, so the edges are listed once, here:
//
//   page load        nothing, unless a table is EMPTY (see hydrateIfEmpty)
//   sign-in          full forced pull
//   reconnect        full forced pull
//   tab regains focus  leads + event tags (catalogue is throttled separately)
//   Download button  full forced pull of the catalogue
//
// A page load deliberately refreshes NOTHING. It used to force the lot, which
// measured 434 KB and six full-table Dexie rewrites per reload; freshness has
// its own edges and does not need the reload to be one of them.
import { useSessionStore } from '@/lib/auth/session';
import { db } from '@/lib/db';

import { syncCatalogue } from './catalogue-precache';
import { connectivity } from './connectivity';
import { refreshEventTagsMirror } from './event-tags-mirror';
import { refreshLeadsMirror } from './leads-mirror';
import { installUnsyncedWorkCounter } from './outbox';
import { startSyncEngine } from './sync-engine';

let started = false;

/**
 * Pull the read-side data (catalogue photos+text, buyers, leads mirror, event tags).
 * Best-effort.
 *
 * `force` says "something happened that means the data may genuinely have
 * changed" and bypasses syncCatalogue's MIN_INTERVAL_MS floor — reconnect and a
 * fresh login qualify. Tab focus does NOT: the Android app-switcher fires
 * `visibilitychange` constantly, and a return after a real absence clears the
 * interval on its own anyway.
 */
function pullAll(options: { force?: boolean } = {}): void {
  void syncCatalogue(options);
  void refreshLeadsMirror();
  void refreshEventTagsMirror();
}

/**
 * What a PAGE LOAD is allowed to do: fill in what is missing, never refresh
 * what is already here.
 *
 * A plain reload used to force the whole pass. Measured on the deployed build:
 * 8 requests / 434 KB, of which `/api/leads` alone was 285 KB, plus a
 * clear-and-replace of six Dexie tables — every single time, including a reload
 * seconds after the last one. None of that is resumability; it is freshness,
 * and freshness already has its own edges (reconnect, sign-in, the readiness
 * panel's "Download everything now"). Paying it on load bought nothing and put
 * six full-table rewrites in the same window as a lead save.
 *
 * So the boot pass now only covers the genuinely cold device — one that has
 * been evicted, or is being opened for the first time on a restored session,
 * where "don't refresh" would otherwise mean "show an empty app". Every count
 * here is a Dexie read, not a request; a warm device issues nothing at all.
 *
 * `syncCatalogue()` is already hydrate-only when unforced — it returns early
 * once the catalogue table has rows — so it needs no guard of its own here.
 *
 * The cost of dropping the forced boot pass, stated plainly: a photo download
 * cut short mid-show no longer resumes on the next app start. It resumes on
 * reconnect, on sign-in, or when someone taps Download everything now — and the
 * readiness panel exists precisely to make that visible before leaving for a
 * show. That trade was the user's call, made deliberately.
 */
async function hydrateIfEmpty(): Promise<void> {
  void syncCatalogue();
  // Both mirrors are full-replace, so "empty" is the only safe read of "cold".
  // An empty leads table on a server that genuinely has no leads re-checks on
  // each load and pulls an empty array back — a few bytes, not worth a flag.
  if ((await db.leads.count()) === 0) void refreshLeadsMirror();
  if ((await db.eventTags.count()) === 0) void refreshEventTagsMirror();
}

export function startOffline(): void {
  if (started) return;
  started = true;

  // 1. The logout guard can now weigh real unsynced work before wiping.
  installUnsyncedWorkCounter();

  // 2. Connectivity edges drive the sync engine's drain + our read-side pulls.
  connectivity.start();
  startSyncEngine();

  // 3. Re-pull on reconnect and on tab focus (Android app-switcher returns).
  connectivity.subscribe(online => {
    if (online) pullAll({ force: true });
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') pullAll();
  });

  // 4. Force a re-pull when a session is newly ESTABLISHED — a sign-in, or a
  //    cookie-backed recovery that flipped `unauthenticated` → `authenticated`
  //    (lib/auth/session.ts `revalidate`). The engine re-drains on its own.
  //
  //    WHICH previous status it came from is the whole point, so the two paths
  //    into `authenticated` are handled separately:
  //
  //      unauthenticated → authenticated   a sign-in. Force: this device may
  //                                        have been someone else's, or away
  //                                        for a week.
  //      loading        → authenticated    a page load restoring a cached
  //                                        session. Hydrate only — this is the
  //                                        common reload, and forcing here
  //                                        would reinstate the whole 434 KB
  //                                        pass one indirection away from the
  //                                        step-5 change below.
  //
  //    Testing the previous status also matters because zustand notifies on
  //    EVERY `set()`, including the connectivity-only writes in session.ts —
  //    without it, an `authenticated` → `authenticated` write would pull.
  let previousStatus = useSessionStore.getState().status;
  useSessionStore.subscribe(state => {
    const from = previousStatus;
    previousStatus = state.status;
    if (state.status !== 'authenticated' || from === 'authenticated') return;
    if (from === 'unauthenticated') pullAll({ force: true });
    else void hydrateIfEmpty();
  });

  // 5. Only for the case where the cached session had already resolved before
  //    startOffline ran; the usual boot path is `loading` and lands on the
  //    subscriber above. Hydrate-only either way. Firing pre-auth would just be
  //    a round of 401s, which is why this stays gated.
  if (useSessionStore.getState().status === 'authenticated') void hydrateIfEmpty();
}

// ── Public barrel ────────────────────────────────────────────────────────────
export {
  enqueueLeadCreate,
  enqueueLeadUpdate,
  enqueueLeadStageChange,
  amendLeadCreate,
  listOutbox,
  getOutboxEntry,
  retryOutboxEntry,
  discardOutboxEntry,
  refreshSyncCounts,
  countUnsyncedWork,
  setResumeTarget,
  takeResumeTarget
} from './outbox';
export type { CardAmendment, CardCapture } from './outbox';
export { refreshLeadsMirror, readMirror, readDrafts, readDraft, getLastSyncAt } from './leads-mirror';
export { refreshEventTagsMirror, readEventTaggedIds } from './event-tags-mirror';
export { scheduleCaptureAutosave, loadCaptureAutosave, clearCaptureAutosave } from './capture-autosave';
export { lookupBuyers, refreshBuyerDirectory } from './buyer-directory';
export { syncCatalogue, IMAGE_CACHE } from './catalogue-precache';
export { flushNow, startSyncEngine, stopSyncEngine } from './sync-engine';
export { useIsOffline, useIsOnline } from './use-online';
export { isOnlineHint } from './connectivity';
export { useSyncStatus, deriveBadgeKind, type SyncBadgeKind } from './sync-status-store';
export { useReadiness, isFullyCached, type CatalogueReadiness } from './readiness-store';
